import * as React from 'react';
import { RefObject, ReactNode, useContext, useEffect, createRef, useState, PropsWithChildren } from 'react';
import { GetJobEntriesStatusTypeEnum, ImportManagementServiceApi } from '../../../../api/import/apis/ImportManagementServiceApi';
import { IntlShape, useIntl } from 'react-intl';
import { searchDelayDefault } from 'Settings/config';
import { getAuthMiddleware } from '../../../../Auth/utils/GetAuthMiddleware';
import { AuthContext } from '../../../../Auth/components/AuthProvider';
import { DataTable, DataTablePageEvent, DataTableSelectionMultipleChangeEvent, DataTableStateEvent } from 'primereact/datatable';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { Layout } from '../../../Layout';
import { Direction } from '../../../../util/types';
import { JobEntryDTO, JobEntryFiltersDTO, PageJobEntryDTO } from 'api/import/models';
import { addLocale, FilterMatchMode, locale } from 'primereact/api';
import { Link } from 'react-router';
import { Button } from 'primereact/button';
import { InputNumber, InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { PageType } from './AdminJobWrapper';

const initialLazyTableState: DataTablePageEvent = {
  first: 0,
  rows: 10,
  page: 0,
  sortField: undefined,
  sortOrder: undefined,
  filters: {
    statusType: {
      value: null,
      matchMode: FilterMatchMode.EQUALS
    },
    isDuplicate: {
      value: null,
      matchMode: FilterMatchMode.EQUALS
    },
    jobId: {
      value: null,
      matchMode: FilterMatchMode.EQUALS
    }
  },
};

interface AdminJobEntriesManagementProps {
  setCurrentPage:  React.Dispatch<React.SetStateAction<PageType>>;
  jobId: number;
  setJobId: React.Dispatch<React.SetStateAction<number>>;
  setPageIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AdminJobEntriesManagement: React.FC<PropsWithChildren<AdminJobEntriesManagementProps>> = (props: PropsWithChildren<AdminJobEntriesManagementProps>): ReactNode => {
  const intl: IntlShape = useIntl();
  const { setCurrentPage, jobId, setJobId, setPageIsLoading } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [jobEntries, setJobEntries] = useState<JobEntryDTO[]>([]);
  const [searchString, setSearchString] = useState<string>('');
  const [selectedJobEntries, setSelectedJobEntries] = useState<JobEntryDTO[]>([]);
  const toast: RefObject<Toast> = createRef<Toast>();
  const isInitialRender = React.useRef<boolean>(true);
  const {actions, authorizer} = useContext(AuthContext);
  const [statusTypeOptions, setStatusTypeOptions] = useState<{ label: string; value: any; }[]>([]);

  const duplicateOptions = [
    { label: intl.formatMessage({id: 'importManagement.filter.all'}), value: null },
    { label: intl.formatMessage({id: 'importJobEntriesManagement.filter.duplicate.true'}), value: true },
    { label: intl.formatMessage({id: 'importJobEntriesManagement.filter.duplicate.false'}), value: false },
  ];

  const initialJobId = jobId;
  const [lazyState, setLazyState] = useState<DataTablePageEvent>(() => ({
    ...initialLazyTableState,
    filters: {
      ...initialLazyTableState.filters,
      jobId: {
        value: initialJobId ?? null,
        matchMode: FilterMatchMode.EQUALS
      }
    }
  }));

  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  } as const;

  const api: ImportManagementServiceApi = new ImportManagementServiceApi().withMiddleware(getAuthMiddleware(actions));

  useEffect(() => {
    const currentLocale = intl.locale;

    addLocale(currentLocale, {
      clear: intl.formatMessage({ id: 'importManagement.filter.clear' }),
      apply: intl.formatMessage({ id: 'importManagement.filter.apply' }),
    });

    locale(currentLocale);
  }, [intl]);

  useEffect((): void => {
    void loadFilters();
  }, []);

  useEffect((): void => {
    void lazyLoadData(searchString, lazyState);
  }, [lazyState]);

  useEffect((): () => void => {
    if(isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    const delayDebounceFn = setTimeout(() => lazyLoadData(searchString, lazyState), searchDelayDefault);
    return (): void => clearTimeout(delayDebounceFn);
  }, [searchString]);

  const loadFilters = (): void => {
    setLoading(true);
    setPageIsLoading(true);

    void api.
      getJobEntriesFilters()
      .then((filters: JobEntryFiltersDTO): void => {
        if(filters) {
          setStatusTypeOptions([
            { label: intl.formatMessage({id: 'importManagement.filter.all'}), value: null },
            ...filters.statusTypes.map(statusTypes => ({
              label: statusTypes,
              value: statusTypes,
            })),
          ]);
        }
        setLoading(false);
        setPageIsLoading(false);
      })
      .catch((): void => {
        setLoading(false);
        setPageIsLoading(false);
        toast.current.show({severity: 'error', summary: 'Error', detail: 'Loading job entry filters failed'});
      });
  };

  const lazyLoadData = (searchParameter: string, dataTablePageEvent: DataTablePageEvent): void => {

    setLoading(true);
    setPageIsLoading(true);

    const statusFilter = dataTablePageEvent.filters?.statusType;

    const statusType: GetJobEntriesStatusTypeEnum =
      statusFilter &&
      !Array.isArray(statusFilter) &&
      'value' in statusFilter
        ? statusFilter.value as GetJobEntriesStatusTypeEnum | null
        : null;
    
    const duplicateFilter = dataTablePageEvent.filters?.isDuplicate;

    const duplicate =
      duplicateFilter &&
      !Array.isArray(duplicateFilter) &&
      'value' in duplicateFilter
        ? duplicateFilter.value as boolean | null
        : null;

    const jobIdFilter = dataTablePageEvent.filters?.jobId;

    const jobId =
      jobIdFilter &&
      !Array.isArray(jobIdFilter) &&
      'value' in jobIdFilter
        ? jobIdFilter.value as number | null
        : null;
    
    void api
      .getJobEntries({
        page: dataTablePageEvent.page,
        size: dataTablePageEvent.rows,
        sort: dataTablePageEvent.sortField || undefined,
        direction: dataTablePageEvent.sortOrder > 0 ? Direction.ASC : Direction.DESC,
        statusType: statusType ?? undefined,
        jobId: jobId ?? undefined,
        isDuplicate: duplicate ?? undefined,
        searchParameter: searchParameter
      })
      .then((jobEntriesDTOs: PageJobEntryDTO): void => {
        if (jobEntriesDTOs) {
          setJobEntries(jobEntriesDTOs.content ?? []);
          setTotalRecords(jobEntriesDTOs.totalElements)
        }
        setLoading(false);
        setPageIsLoading(false);
      })
      .catch((): void => {
        setLoading(false);
        setPageIsLoading(false);
        toast.current.show({severity: 'error', summary: 'Error', detail: 'Loading job entries failed'});
      });
  };

  const onPageChange = (e: DataTableStateEvent): void => {setLazyState(e); setSelectedJobEntries([]);};

  const onSortChange = (e: DataTableStateEvent): void => {setLazyState(e); setSelectedJobEntries([]);};

  const onFilterChange = (e: DataTableStateEvent): void => {setLazyState({...e, first: 0}); setSelectedJobEntries([]);};

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>): void => {setSearchString(e.target.value); setSelectedJobEntries([]);};

  const header = (
    <div className="flex justify-content-between my-3">
      <div className="flex gap-2">
        <Button
          label={intl.formatMessage({id: 'importJobEntriesManagement.button.goBack'})}
          onClick={() => {
            setJobId(undefined);
            setCurrentPage('JOBS');
          }}
          icon="pi pi-arrow-left"
        />
        <Button
          label={intl.formatMessage({id: 'importJobEntriesManagement.button.restartImportForSelected'})}
          severity={'secondary'}
          outlined={true}
          disabled={selectedJobEntries.length < 1}
          onClick={() => confirm(selectedJobEntries)}
        />
        <Button
          label={intl.formatMessage({id: 'importJobEntriesManagement.button.removeSelection'})}
          onClick={() => setSelectedJobEntries([])}
          severity={'warning'}
          outlined={true}
          icon="pi pi-eraser"
          disabled={selectedJobEntries.length < 1}
        />
      </div>
      <div className="flex gap-4">
        <Button
          label={intl.formatMessage({id: 'importManagement.button.removeFilters'})}
          onClick={() => setLazyState(initialLazyTableState)}
          icon="pi pi-undo"
          disabled={lazyState.filters?.statusType.value === null && lazyState.filters?.jobId.value === null && lazyState.filters?.isDuplicate.value === null }
        />
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search"/>
          <InputText value={searchString} onChange={onGlobalFilterChange} type='search'
            placeholder={intl.formatMessage({id: 'search.placeholder'})}/>
        </IconField>
      </div>
    </div>
  );

  const statusFilter = (
    options: ColumnFilterElementTemplateOptions
  ): React.ReactNode => (
    <Dropdown
      value={options.value ?? null}
      options={statusTypeOptions}
      optionLabel="label"
      optionValue="value"
      onChange={(e: DropdownChangeEvent) => options.filterCallback(e.value)}
      showClear
      style={{ width: '100%' }}
    />
  );

  const duplicateFilter = (
    options: ColumnFilterElementTemplateOptions
  ): React.ReactNode => (
    <Dropdown
      value={options.value ?? null}
      options={duplicateOptions}
      optionLabel="label"
      optionValue="value"
      onChange={(e: DropdownChangeEvent) => options.filterCallback(e.value)}
      showClear
      style={{ width: '100%' }}
    />
  );

  const jobIdFilter = (
    options: ColumnFilterElementTemplateOptions
  ): React.ReactNode => (
    <InputNumber
      value={options.value}
      onValueChange={(e: InputNumberValueChangeEvent): void =>
        options.filterCallback(e.value)
      }
      useGrouping={false}
      placeholder={intl.formatMessage({id: 'importJobEntriesManagement.jobId.placeholder'})}
      min={0}
      className='w-full'
      inputClassName='w-full'
    />
  );

  const confirm = (entries: JobEntryDTO[]): void => {
    confirmDialog({
      header: intl.formatMessage({id: 'importJobEntriesManagement.reimport.modal.header'}),
      message: selectedJobEntries.length > 1 ?
        intl.formatMessage({id: 'importJobEntriesManagement.reimport.modal.multiple.message(count)'}, {count: selectedJobEntries.length})
        : intl.formatMessage({id: 'importJobEntriesManagement.reimport.modal.single.message'}),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-primary',
      acceptLabel: intl.formatMessage({id: 'importJobEntriesManagement.reimport.modal.button.reimport'}),
      rejectLabel: intl.formatMessage({id: 'importJobEntriesManagement.reimport.modal.button.abort'}),
      accept: () => reimportJobEntries(entries),
      reject: () => {}
    });
  };

  const reimportJobEntries = (entries: JobEntryDTO[]): void => {
    setLoading(true);
    setPageIsLoading(true);
    
    void api
      .reimportJobEntries({
        requestBody: entries.map((entry) => entry.id)
      })
      .then((updatedJobEntriesDTOs: JobEntryDTO[]): void => {
        setJobEntries(prevJobEntries =>
          prevJobEntries.map(jobEntry => {
            const updatedJobEntry = updatedJobEntriesDTOs.find(
              updated => updated.id === jobEntry.id
            );

            return updatedJobEntry ?? jobEntry;
          })
        )
        setSelectedJobEntries([]);
        setLoading(false);
        setPageIsLoading(false);
      })
      .catch((): void => {
        setLoading(false);
        setPageIsLoading(false);
        toast.current.show({severity: 'error', summary: 'Error', detail: 'Reimport job entries failed'});
      });
  }

  return (
    <Layout
      loading={loading}
      loadingOverlay={loading}
      isAdminJobEntriesManagement={true}
    >
      <Toast ref={toast}/>
      <h1 style={{fontSize: 'calc(15px + 1.8vw)'}} className={'md:text-3xl m-1 p-1'}>
        {intl.formatMessage({id: 'importJobEntriesManagement.title'})}
      </h1>
      <DataTable
        value={jobEntries}
        lazy={true}
        stripedRows={true}
        showGridlines={true}
        dataKey="id"
        paginator={true}
        first={lazyState.first}
        totalRecords={totalRecords}
        sortField={lazyState.sortField}
        sortOrder={lazyState.sortOrder}
        filters={lazyState.filters}
        rowsPerPageOptions={[10, 25, 50]}
        rows={lazyState.rows}
        header={header}
        editMode="row"
        onPage={onPageChange}
        onSort={onSortChange}
        onFilter={onFilterChange}
        tableStyle={{
          width: '100%',
          tableLayout: 'fixed',
        }}
        selectionMode="checkbox"
        selection={selectedJobEntries}
        onSelectionChange={(e: DataTableSelectionMultipleChangeEvent<JobEntryDTO[]>) => setSelectedJobEntries(e.value)}
      >
        <Column selectionMode="multiple" style={{width: '3%'}}/>
        <Column field="id" style={{width:'5%'}}
          bodyStyle={{
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word'
          }}
          header={intl.formatMessage({id: 'importJobEntriesManagement.header.id'})} sortable
        />
        <Column field="scopus" style={{width:'7%'}}
          bodyStyle={{
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word'
          }}
          header={intl.formatMessage({id: 'importJobEntriesManagement.header.scopus'})}
        />
        <Column field="wos" style={{width:'7%'}}
          bodyStyle={{
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word'
          }}
          header={intl.formatMessage({id: 'importJobEntriesManagement.header.wos'})}
        />
        <Column field="pmid" style={{width:'6%'}}
          bodyStyle={{
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word'
          }}
          header={intl.formatMessage({id: 'importJobEntriesManagement.header.pmid'})}
        />
        <Column field="doi" style={{width:'7%'}}
          bodyStyle={{
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
          header={intl.formatMessage({id: 'importJobEntriesManagement.header.doi'})}
        />
        <Column field="isbn" style={{width:'7%'}}
          bodyStyle={{
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word'
          }}
          header={intl.formatMessage({id: 'importJobEntriesManagement.header.isbn'})}
        />
        <Column field="openAlex" style={{width:'7%'}}
          bodyStyle={{
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word'
          }}
          header={intl.formatMessage({id: 'importJobEntriesManagement.header.openAlex'})}
        />
        <Column field="mms" style={{width:'5%'}}
          bodyStyle={{
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word'
          }}
          header={intl.formatMessage({id: 'importJobEntriesManagement.header.mms'})}
          body={(rowData: JobEntryDTO) =>
            rowData.mms ? (
              <Link to={`/work/${rowData.mms}`} className='text-primary-800'>
                {rowData.mms}
              </Link>
            ) : (
              <></>
            )
          }
        />
        <Column
          field="jobId"
          style={{ width: '7%' }}
          bodyStyle={{
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
          header={intl.formatMessage({
            id: 'importJobEntriesManagement.header.jobId',
          })}
          sortable
          filter
          filterElement={jobIdFilter}
          filterMatchMode={FilterMatchMode.EQUALS}
          showFilterMatchModes={false}
          filterMenuClassName="import-filter-menu"
        />
        <Column
          field="statusType"
          style={{ width: '6%' }}
          bodyStyle={{
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
          body={(rowData: JobEntryDTO) =>
            rowData.statusType === 'ERROR' ? (
              <div className="text-red-500">{rowData.statusType}</div>
            ) : (
              <div>{rowData.statusType}</div>
            )
          }
          filter
          filterElement={statusFilter}
          filterMatchMode={FilterMatchMode.EQUALS}
          showFilterMatchModes={false}
          header={intl.formatMessage({id: 'importJobEntriesManagement.header.statusType'})}
          filterMenuClassName="import-filter-menu"
        />
        <Column
          field="isDuplicate"
          style={{ width: '6.5%' }}
          body={(rowData: JobEntryDTO) =>
            rowData.isDuplicate ? (
              <i className="pi pi-check" />
            ) : (
              <i className="pi pi-times" />
            )
          }
          bodyStyle={{ textAlign: 'center' }}
          header={intl.formatMessage({
            id: 'importJobEntriesManagement.header.duplicate',
          })}
          filter
          filterElement={duplicateFilter}
          filterMatchMode={FilterMatchMode.EQUALS}
          showFilterMatchModes={false}
          filterMenuClassName="import-filter-menu"
        />
        <Column field="message" style={{maxWidth:'16%'}}
          bodyStyle={{
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word'
          }}
          header={intl.formatMessage({id: 'importJobEntriesManagement.header.message'})}
        />
        <Column style={{width:'10.5%'}}
          bodyStyle={{ textAlign: 'center' }}
          body={(rowData: JobEntryDTO) =>
            authorizer.hasRole('ROLE_ADMIN') || (authorizer.hasRole('ROLE_EDITOR') && (rowData.statusType.includes('ERROR') || rowData.statusType.includes('ERROR_WITH_SUCCESS'))) ? (
              <div className='my-2'>
                <Button className='text-sm' onClick={() => confirm([rowData])}>
                  {intl.formatMessage({id: 'importJobEntriesManagement.button.restartImport'})}
                </Button>
                {rowData.reimportOn &&
                  <div className='text-sm mt-2'>
                    {intl.formatMessage({id: 'importJobEntriesManagement.reimport.last'})}&nbsp;{rowData.reimportOn.toLocaleDateString("de-DE", options)}
                  </div>
                }
              </div>
            ) : (<></>)
          }
        />
      </DataTable>
    </Layout>
  );
}

export default AdminJobEntriesManagement;
